import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, Download, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import Papa from 'papaparse';
import { ConditionalSkipService, type ConditionalSkipBulkData } from '@/services/conditional-skip-service';

interface ConditionalSkipCsvImportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}

interface CsvRow {
  'Bot Name'?: string;
  'bot_name'?: string;
  Status?: string;
  status?: string;
  Threshold?: string;
  threshold?: string;
  'Max Loss Streak'?: string;
  'max_loss_streak'?: string;
  'Max Loss Behavior'?: string;
  'max_loss_behavior'?: string;
  'Forward URL'?: string;
  'forward_url'?: string;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  data: ConditionalSkipBulkData | null;
}

export function ConditionalSkipCsvImport({ open, onOpenChange, onImportComplete }: ConditionalSkipCsvImportProps) {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [parsedData, setParsedData] = useState<CsvRow[]>([]);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const csvTemplate = [
    ['Bot Name', 'Status', 'Threshold', 'Max Loss Streak', 'Max Loss Behavior', 'Forward URL'],
    ['MyBot1', 'true', '3', '5', 'reset_streak', 'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/signal-router'],
    ['MyBot2', 'enabled', '0', '10', 'lock_gate', 'https://example.com/webhook'],
    ['MyBot3', 'false', '2', '', 'reset_streak', '']
  ];

  const downloadTemplate = () => {
    const csv = Papa.unparse(csvTemplate);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'conditional_skip_settings_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    parseCsvFile(selectedFile);
  };

  const normalizeHeaders = (data: CsvRow[]): CsvRow[] => {
    return data.map(row => {
      const normalizedRow: CsvRow = { ...row };
      
      // Map user-friendly headers to technical headers
      if (row['Bot Name']) normalizedRow.bot_name = row['Bot Name'];
      if (row.Status) normalizedRow.status = row.Status;
      if (row.Threshold) normalizedRow.threshold = row.Threshold;
      if (row['Max Loss Streak']) normalizedRow.max_loss_streak = row['Max Loss Streak'];
      if (row['Max Loss Behavior']) normalizedRow.max_loss_behavior = row['Max Loss Behavior'];
      if (row['Forward URL']) normalizedRow.forward_url = row['Forward URL'];
      
      return normalizedRow;
    });
  };

  const parseCsvFile = (file: File) => {
    Papa.parse<CsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          toast.error('CSV parsing errors found');
          console.error('CSV errors:', results.errors);
          return;
        }

        const normalizedData = normalizeHeaders(results.data);
        setParsedData(normalizedData);
        validateData(normalizedData);
        setShowPreview(true);
      },
      error: (error) => {
        toast.error('Failed to parse CSV file');
        console.error('CSV parse error:', error);
      }
    });
  };

  const validateData = async (data: CsvRow[]) => {
    const results: ValidationResult[] = [];

    for (let index = 0; index < data.length; index++) {
      const row = data[index];
      const errors: string[] = [];
      
      // Get bot name from either format
      const botName = row.bot_name || row['Bot Name'];
      
      // Validate required fields
      if (!botName?.trim()) {
        errors.push('Bot Name is required');
      }
      
      // Validate threshold
      const thresholdStr = row.threshold || row.Threshold;
      if (!thresholdStr?.trim()) {
        errors.push('Threshold is required');
      } else {
        const threshold = parseInt(thresholdStr);
        if (isNaN(threshold) || threshold < 0) {
          errors.push('Threshold must be a non-negative integer');
        }
      }
      
      // Validate max loss streak (optional)
      const maxLossStreakStr = row.max_loss_streak || row['Max Loss Streak'];
      if (maxLossStreakStr?.trim()) {
        const maxLossStreak = parseInt(maxLossStreakStr);
        if (isNaN(maxLossStreak) || maxLossStreak <= 0) {
          errors.push('Max Loss Streak must be a positive integer');
        }
      }
      
      // Validate max loss behavior
      const maxLossBehavior = row.max_loss_behavior || row['Max Loss Behavior'] || 'reset_streak';
      if (!['reset_streak', 'lock_gate'].includes(maxLossBehavior)) {
        errors.push('Max Loss Behavior must be "reset_streak" or "lock_gate"');
      }
      
      // Validate status
      const statusStr = row.status || row.Status;
      let enabled = true;
      if (statusStr) {
        const normalizedStatus = statusStr.toLowerCase();
        if (['false', '0', 'disabled'].includes(normalizedStatus)) {
          enabled = false;
        } else if (!['true', '1', 'enabled'].includes(normalizedStatus)) {
          errors.push('Status must be true/false, enabled/disabled, or 1/0');
        }
      }

      const valid = errors.length === 0;
      let settingData: ConditionalSkipBulkData | null = null;

      if (valid && botName) {
        // Check if bot exists (this would be done in the service)
        settingData = {
          bot_name: botName.trim(),
          enabled: enabled,
          loss_streak_threshold: parseInt(thresholdStr || '0'),
          max_loss_streak: maxLossStreakStr?.trim() ? parseInt(maxLossStreakStr) : null,
          max_loss_behavior: maxLossBehavior as 'reset_streak' | 'lock_gate',
          forward_url: (row.forward_url || row['Forward URL'])?.trim() || 'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/signal-router'
        };
      }

      results.push({ valid, errors, data: settingData });
    }

    setValidationResults(results);
  };

  const handleImport = async () => {
    const validRows = validationResults.filter(result => result.valid && result.data);
    
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);
    setImportProgress(0);

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      for (let i = 0; i < validRows.length; i++) {
        const result = validRows[i];
        if (!result.data) continue;

        try {
          await ConditionalSkipService.createBulkSetting(result.data);
          successCount++;
        } catch (error: any) {
          console.error(`Failed to import ${result.data.bot_name}:`, error);
          errorCount++;
          errors.push(`${result.data.bot_name}: ${error.message}`);
        }

        setImportProgress(((i + 1) / validRows.length) * 100);
      }

      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} conditional skip settings`);
        if (errorCount > 0) {
          toast.warning(`${errorCount} rows failed to import. Check console for details.`);
        }
        onImportComplete();
        onOpenChange(false);
        resetState();
      } else {
        toast.error('No rows were imported successfully');
      }
    } catch (error) {
      toast.error('Import failed');
      console.error('Import error:', error);
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const resetState = () => {
    setFile(null);
    setParsedData([]);
    setValidationResults([]);
    setShowPreview(false);
    setImportProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = validationResults.filter(r => r.valid).length;
  const invalidCount = validationResults.length - validCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Conditional Skip Settings from CSV</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {/* Template Download */}
          <Alert>
            <Download className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div>
                  <p>Download the CSV template to ensure proper formatting</p>
                  <p className="text-xs mt-1">
                    <strong>Max Loss Behavior:</strong> Use "reset_streak" (default) or "lock_gate"
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>
            </AlertDescription>
          </Alert>

          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="csv-file">Select CSV File</Label>
            <div className="flex items-center gap-2">
              <Input
                ref={fileInputRef}
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={importing}
              />
              {file && (
                <Button variant="outline" size="sm" onClick={resetState}>
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Import Progress */}
          {importing && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Importing settings...</span>
                <span>{Math.round(importProgress)}%</span>
              </div>
              <Progress value={importProgress} />
            </div>
          )}

          {/* Preview and Validation */}
          {showPreview && !importing && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Import Preview</h3>
                <div className="flex items-center gap-4">
                  <Badge variant="default" className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Valid: {validCount}
                  </Badge>
                  {invalidCount > 0 && (
                    <Badge variant="destructive">
                      <XCircle className="h-3 w-3 mr-1" />
                      Invalid: {invalidCount}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="border rounded-lg max-h-64 overflow-auto">
                <div className="space-y-2 p-4">
                  {validationResults.map((result, index) => (
                    <div
                      key={index}
                      className={`border rounded p-3 ${
                        result.valid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {result.valid ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <span className="font-medium">
                            {result.data?.bot_name || parsedData[index]?.bot_name || parsedData[index]?.['Bot Name'] || `Row ${index + 1}`}
                          </span>
                        </div>
                        {result.data && (
                          <div className="text-sm text-muted-foreground">
                            Threshold: {result.data.loss_streak_threshold} • {result.data.max_loss_behavior}
                          </div>
                        )}
                      </div>
                      {result.errors.length > 0 && (
                        <div className="mt-2 text-sm text-red-600">
                          <ul className="list-disc list-inside">
                            {result.errors.map((error, i) => (
                              <li key={i}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
            Cancel
          </Button>
          {showPreview && validCount > 0 && (
            <Button onClick={handleImport} disabled={importing}>
              <Upload className="h-4 w-4 mr-2" />
              Import {validCount} Settings
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}